import type { z } from "zod";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiOptions<S extends z.ZodType | undefined> extends RequestInit {
  /** Si se pasa, la respuesta se valida contra este esquema de @ir/contracts. */
  schema?: S;
}

export async function apiFetch<S extends z.ZodType | undefined = undefined>(
  path: string,
  options: ApiOptions<S> = {},
): Promise<S extends z.ZodType ? z.infer<S> : unknown> {
  // `body` se queda dentro de `rest` (no se desestructura aparte): bajo
  // exactOptionalPropertyTypes, reescribirlo como propiedad explícita del
  // nuevo objeto (`body,`) lo tipa `BodyInit | null | undefined`, que ya no
  // encaja en `RequestInit.body?: BodyInit | null` (sin `undefined`
  // explícito) aunque el valor de origen sea idéntico. Pasarlo únicamente a
  // través de `...rest` conserva su optionality tal cual la declara
  // RequestInit y compila limpio, sin cambiar qué body viaja en la petición.
  const { schema, headers, ...rest } = options;

  // `new Headers(headers).entries()` en vez de esparcir `headers` tal cual:
  // RequestInit.headers es `HeadersInit`, que incluye `string[][]`,
  // y @typescript-eslint/no-misused-spread marca (con razón) que esparcir
  // un array dentro de un objeto produce claves de índice ("0", "1", ...)
  // en vez de fusionar pares clave/valor. El constructor Headers normaliza
  // las tres formas de HeadersInit (Headers, Record, string[][]) antes de
  // convertirlas a un objeto plano seguro de esparcir.
  const response = await fetch(path, {
    ...rest,
    credentials: "include",
    headers: {
      ...(rest.body && !(rest.body instanceof FormData)
        ? { "content-type": "application/json" }
        : {}),
      ...Object.fromEntries(new Headers(headers).entries()),
    },
  });

  if (response.status === 204) {
    return null as never;
  }

  const payload: unknown = response.headers
    .get("content-type")
    ?.includes("application/json")
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    const error = payload as { error?: { code?: string; message?: string } };
    throw new ApiError(
      response.status,
      error.error?.code ?? "unknown",
      error.error?.message ?? "Ocurrió un error inesperado",
    );
  }

  return (schema ? schema.parse(payload) : payload) as never;
}

export function buildQuery(params: Record<string, string | number | boolean | undefined>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export interface UploadOptions<S extends z.ZodType | undefined> {
  schema?: S;
  /** Se llama con los bytes enviados y el total, mientras sube. */
  onProgress?: (loaded: number, total: number) => void;
}

/**
 * Sube un archivo con indicador de avance.
 *
 * No usa `fetch` porque no expone progreso de subida: el cuerpo de la
 * petición no emite eventos. Para un HTML de 3.6 MB por VPN eso es la
 * diferencia entre una barra y una pantalla congelada. La forma del error
 * es la misma que la de apiFetch, para que quien llame no distinga.
 *
 * No admite cancelar una subida en curso: no devuelve ni el `xhr` ni un
 * método `abort()`, y no acepta una `AbortSignal`. Si un formulario
 * necesita cancelar, esta función tendrá que ampliarse entonces -no antes,
 * porque nada lo pide todavía (Task 11 del plan).
 */
export function apiUpload<S extends z.ZodType | undefined = undefined>(
  path: string,
  file: File,
  options: UploadOptions<S> = {},
): Promise<S extends z.ZodType ? z.infer<S> : unknown> {
  return new Promise((resolve, reject) => {
    const body = new FormData();
    body.set("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", path);
    xhr.withCredentials = true;

    // Se pone en `true` en cuanto la promesa se asienta (resuelve o
    // rechaza), para que un evento de avance tardío no vuelva a llamar a
    // onProgress: un proxy con client_max_body_size puede responder 413
    // antes de que el cliente termine de emitir el cuerpo, y el navegador
    // sigue mandando eventos de la subida después de ese rechazo.
    let settled = false;

    // Este addEventListener va ANTES de xhr.send(body) a propósito: el
    // estándar XHR fija el "upload listener flag" al llamar a send, así que
    // si el registro bajara de sitio en un refactor, un navegador real
    // dejaría de emitir eventos de avance de la subida -y ninguna prueba lo
    // notaría, porque el doble no modela ese flag-.
    xhr.upload.addEventListener("progress", (event) => {
      if (!settled && event.lengthComputable) options.onProgress?.(event.loaded, event.total);
    });

    xhr.addEventListener("error", () => {
      settled = true;
      reject(new ApiError(0, "network_error", "No se pudo conectar con el servidor"));
    });

    // El otro evento terminal de XHR: si el navegador aborta la petición
    // (navegación, cierre de pestaña), sin este listener la promesa se
    // quedaría pendiente para siempre, igual que un throw sin capturar en
    // "load".
    xhr.addEventListener("abort", () => {
      settled = true;
      reject(new ApiError(0, "upload_aborted", "Se canceló la subida"));
    });

    xhr.addEventListener("load", () => {
      // Todo el cuerpo va envuelto: un throw aquí ocurre dentro del
      // manejador de un evento, no del ejecutor de la promesa, así que sin
      // este try/catch no rechaza -deja la promesa pendiente para siempre-.
      // Pueden lanzar tanto el acceso al cuerpo de error (si el JSON es
      // válido pero no el objeto esperado, p. ej. `null`) como
      // options.schema.parse (ZodError si la respuesta no valida).
      try {
        let payload: unknown = xhr.responseText;
        try {
          payload = JSON.parse(xhr.responseText) as unknown;
        } catch {
          // Respuesta sin JSON: se deja el texto crudo para el mensaje.
        }

        if (xhr.status < 200 || xhr.status >= 300) {
          const error = payload as { error?: { code?: string; message?: string } };
          reject(
            new ApiError(
              xhr.status,
              error.error?.code ?? "unknown",
              error.error?.message ?? "Ocurrió un error inesperado",
            ),
          );
          return;
        }

        resolve((options.schema ? options.schema.parse(payload) : payload) as never);
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)));
      } finally {
        settled = true;
      }
    });

    xhr.send(body);
  });
}
